import { Alert, Typography } from '@mui/material'
import { useController, useFormContext } from 'react-hook-form'

import { Box } from '@/components/ui'
import type { ProductFlagFormValues } from '@/definitions/productFlags'
import { useConfirm } from '@/hooks/useConfirm'

import { useFlagChange } from '../../hooks'
import type { FlagRowState } from '../../utils/buildFlagSections'
import { enabledDependentLabelsOf } from '../../utils/flagDependencies'
import { ProductFlagRow } from '../ProductFlagRow'
import { TwilioAccountSidRow } from '../TwilioAccountSidRow'

/** Reads as a sentence: "A, B, and C". */
const listLabels = (labels: readonly string[]): string =>
  new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(labels)

/** A switch bound to its form field. */
const ConnectedFlagSwitch = ({ row }: { row: FlagRowState }) => {
  const { control } = useFormContext<ProductFlagFormValues>()
  const { field: bound } = useController({ control, name: row.field })
  const changeFlag = useFlagChange()

  return (
    <ProductFlagRow
      label={row.label}
      checked={bound.value === true}
      isEditable={row.isEditable}
      requirementLabel={row.requirementLabel}
      isRequirementMet={row.isRequirementMet}
      errorMessage={row.errorMessage}
      onChange={(checked) => changeFlag(row.field, checked)}
    />
  )
}

const POCKETFLOWS_DELETION_WARNING =
  "Saving this change deletes the organization's Pocketflows business and all of " +
  'its email templates. Pocketflows cannot recover them.'

const POCKETFLOWS_RE_ENABLE_NOTE =
  'Turning Pocketflows back on later creates a new business, with none of the ' +
  'templates the practice built.'

/**
 * Pocketflows: the same switch, but it asks before it goes off.
 *
 * Every other switch here is harmless to flip and reversible. This one destroys
 * the practice's email templates, which are its own work and which Pocketflows
 * cannot restore, so it is asked about where the decision is made rather than at
 * save time. Declining never stages anything, so the switch stays where it was and
 * nothing else staged on the tab is disturbed. The wording is about saving because
 * the switch only stages the change — nothing is deleted until the form is saved.
 *
 * The product flags going off with it are named from the dependency map rather than
 * spelled out here, so a product flag added behind Pocketflows is named by this dialog
 * too instead of leaving it promising less than the save will do.
 *
 * Turning the product flag on is not guarded — it provisions rather than destroys — and
 * does not bring the product flags behind it back, which is the operator's own call to
 * make.
 */
const ConnectedPocketflowsSwitch = ({ row }: { row: FlagRowState }) => {
  const { control, getValues } = useFormContext<ProductFlagFormValues>()
  const { field: bound } = useController({ control, name: row.field })
  const changeFlag = useFlagChange()
  const confirm = useConfirm()

  const handleChange = async (checked: boolean) => {
    if (checked) {
      changeFlag(row.field, true)
      return
    }

    const alsoGoingOff = enabledDependentLabelsOf(getValues(), row.field)

    const confirmed = await confirm({
      title: 'Turn off Pocketflows for this practice?',
      body: (
        <Box variant='flexColumn' gap='md'>
          <Alert severity='error'>{POCKETFLOWS_DELETION_WARNING}</Alert>
          {alsoGoingOff.length > 0 && (
            <Typography variant='body2'>
              {listLabels(alsoGoingOff)} {alsoGoingOff.length === 1 ? 'is' : 'are'} turned
              off with it.
            </Typography>
          )}
          <Typography variant='body2' color='text.secondary'>
            {POCKETFLOWS_RE_ENABLE_NOTE}
          </Typography>
        </Box>
      ),
      confirmLabel: 'Turn off Pocketflows',
      destructive: true,
    })

    if (confirmed) {
      changeFlag(row.field, false)
    }
  }

  return (
    <ProductFlagRow
      label={row.label}
      checked={bound.value === true}
      isEditable={row.isEditable}
      requirementLabel={row.requirementLabel}
      isRequirementMet={row.isRequirementMet}
      errorMessage={row.errorMessage}
      onChange={handleChange}
    />
  )
}

/** Texting: a switch and the account SID it depends on, both staged. */
const ConnectedTwilioRow = ({ row }: { row: FlagRowState }) => {
  const { control } = useFormContext<ProductFlagFormValues>()
  const enabled = useController({ control, name: 'twilio_enabled' })
  const accountSid = useController({ control, name: 'twilio_account_sid' })
  const changeFlag = useFlagChange()

  return (
    <TwilioAccountSidRow
      label={row.label}
      checked={enabled.field.value}
      accountSid={accountSid.field.value}
      isEditable={row.isEditable}
      isRequirementMet={row.isRequirementMet}
      errorMessage={row.errorMessage}
      onCheckedChange={(checked) => changeFlag('twilio_enabled', checked)}
      onAccountSidChange={accountSid.field.onChange}
    />
  )
}

export interface ProductFlagSectionProps {
  title: string
  /** False when a lower package is selected, which washes the whole section out. */
  isEditable: boolean
  rows: FlagRowState[]
}

/** One package's product flags. */
export const ProductFlagSection = ({
  title,
  isEditable,
  rows,
}: ProductFlagSectionProps) => (
  <Box variant='flexColumn' gap='sm'>
    <Typography variant='overline' color={isEditable ? 'text.secondary' : 'text.disabled'}>
      {title}
    </Typography>

    {rows.map((row) => {
      if (row.isTwilioAccount) {
        return <ConnectedTwilioRow key={row.label} row={row} />
      }
      if (row.isPocketflowsBusiness) {
        return <ConnectedPocketflowsSwitch key={row.label} row={row} />
      }
      return <ConnectedFlagSwitch key={row.label} row={row} />
    })}
  </Box>
)
